// client/src/components/pages/LexiStar/RecomputeJobs/RecomputeJobDetail.tsx  (converted from .jsx
// by ST-00287, CLAUDE.md TS rule)
//
// One recompute job and its before/after diff (LS-09 §5.19; T4-S12):
//   state + progress (polled while the engine works; `lexistar:recompute`
//   refetches on a state change), the headline deltas, the signed-off trips
//   the recompute touches — listed apart, because publishing turns them into
//   amendment requests for a second user instead of changing them — and the
//   changed records with old and new values side by side (table or map).
// Publish is the second user's sign-off (SR-21): disabled for the requester
// and without lexistar.recompute.signoff, always saying why. Discard leaves
// history untouched. A published job stays viewable with its diff.
// ST-00287: a job that "completed with errors" (stage errors the engine isolated
// during the replay) says so on its chip and lists the errors; its sign-off box
// lists them again and publishes only once the second user ticks that they
// reviewed them (`acknowledge_errors: true`). Who acknowledged is shown after.

import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaExclamationTriangle, FaTrashAlt, FaSyncAlt } from 'react-icons/fa';
import SegmentedControl from '../../../common/SegmentedControl';
import { useAuth } from '../../../../context/AuthContext';
import {
  getRecomputeJob,
  getRecomputeDiff,
  signOffRecomputeJob,
  discardRecomputeJob,
  pickObject,
} from '../../../../services/lexistarAssurance';
import { ErrorBanner, NoticeBanner, LoadingState, cardClasses } from '../StatusBoard/atoms';
import { fmtDateTime, fmtNum } from '../StatusBoard/format';
import {
  KpiCard as KpiCardJs,
  Modal,
  ProgressBar,
  InlineError,
  buttonClasses,
} from '../ReviewQueue/assuranceAtoms';
import { errorMessage } from '../ReviewQueue/assuranceShared';
import {
  jobStateMeta,
  isActiveJob,
  progressRatio,
  publishGate,
  triggerLabel,
  scopeSummary,
  headlineCards,
  splitDiffRows,
  stageErrorsOf,
  completedWithErrors,
  needsErrorAcknowledgement,
  stageErrorSummary,
  stageLabel,
  truckOf,
  TABLE_LABELS,
  DISCARDABLE,
  type DiffTableCount,
  type SplitDiff,
} from './recomputeLogic';
import DiffTable from './DiffTable';
import DiffMap from './DiffMap';
import type { RecomputeJob, RecomputeStageErrors } from '../../../../types/lexistar-recompute';

// Typed views of the JS atoms (their props are inferred from the .jsx; the ReviewQueue.tsx pattern).
const KpiCard = KpiCardJs as unknown as ComponentType<{
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'default' | 'warn';
}>;

const POLL_MS = 5000;
const CHANGE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'changed', label: 'Changed' },
  { value: 'added', label: 'Added' },
  { value: 'removed', label: 'Removed' },
];

/**
 * The job's state; `withErrors` (ST-00287) adds the "with errors" mark of a job that
 * completed with stage errors, so the list and the page tell it apart from a clean one.
 */
export function JobStateChip({ state, withErrors = false }: { state: string; withErrors?: boolean }) {
  const meta = jobStateMeta(state);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${state === 'awaiting_signoff' || state === 'computed' ? 'bg-slate-900 text-white' : state === 'failed' ? 'bg-surface text-red-700 ring-1 ring-inset ring-red-400' : 'bg-slate-100 text-slate-700'}`}
        data-state={state}
      >
        <span aria-hidden="true">{meta.glyph}</span>
        {meta.label}
      </span>
      {withErrors && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-300"
          data-with-errors="true"
        >
          <FaExclamationTriangle size={9} aria-hidden="true" />
          with errors
        </span>
      )}
    </span>
  );
}

/**
 * ST-00287: the stage errors of a job, as a list of what threw for which truck. `compact`
 * (the sign-off box) caps the list; the page shows every kind the engine recorded.
 */
export function StageErrorList({ errors, compact = false }: { errors: RecomputeStageErrors; compact?: boolean }) {
  const shown = compact ? errors.items.slice(0, 5) : errors.items;
  const more = errors.items.length - shown.length;
  return (
    <div className="space-y-2">
      <ul className="divide-y divide-amber-200 rounded-lg border border-amber-200 bg-surface" aria-label="Stage errors">
        {shown.map((e, i) => (
          <li key={`${e.partition}|${e.asset_key}|${e.stage}|${e.phase}|${i}`} className="px-3 py-2 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
              <span className="font-semibold text-slate-900">
                {truckOf(e.asset_key)} · {stageLabel(e.stage)}
              </span>
              <span className="text-xs text-slate-600 tabular-nums">
                {fmtNum(e.count)}× {e.first_at ? `· ${fmtDateTime(e.first_at)}` : ''}
                {e.last_at && e.last_at !== e.first_at ? ` → ${fmtDateTime(e.last_at)}` : ''}
              </span>
            </div>
            <p className="mt-0.5 font-mono text-xs text-slate-700 break-words">{e.message}</p>
          </li>
        ))}
      </ul>
      {(more > 0 || errors.truncated) && (
        <p className="text-xs text-slate-600">
          {more > 0 ? `${fmtNum(more)} more kind(s) are listed on the job's page. ` : ''}
          {errors.truncated ? 'The engine recorded the first 50 kinds; the counts include every error.' : ''}
        </p>
      )}
    </div>
  );
}

interface RecomputeJobDetailProps {
  id: string;
  onChanged?: () => void;
  recomputeSignal?: unknown;
}

const EMPTY_DIFF: SplitDiff = { rows: [], signedOff: [], pagination: null, tables: [] };

export default function RecomputeJobDetail({ id, onChanged, recomputeSignal }: RecomputeJobDetailProps) {
  const { user, hasPermission } = useAuth();
  const canRun = hasPermission('lexistar.recompute.run');
  const canSignOff = hasPermission('lexistar.recompute.signoff');
  const [job, setJob] = useState<RecomputeJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [diff, setDiff] = useState<SplitDiff>(EMPTY_DIFF);
  const [diffLoading, setDiffLoading] = useState(false);
  const [table, setTable] = useState('');
  const [change, setChange] = useState('');
  const [page, setPage] = useState(1);
  const [view, setView] = useState('table');
  const [confirm, setConfirm] = useState<'publish' | 'discard' | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const loadJob = useCallback(
    async ({ quiet = false } = {}) => {
      try {
        if (!quiet) setLoading(true);
        const res = await getRecomputeJob(id);
        setJob(pickObject(res, 'job', 'recompute_job') as RecomputeJob | null);
        setError(null);
      } catch (err) {
        if (!quiet)
          setError(
            (err as { response?: { status?: number } }).response?.status === 404
              ? 'Recompute job not found, or not visible to your organisation.'
              : errorMessage(err, 'Failed to load the recompute job.')
          );
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  // Progress is polled (contract §5.2: no progress events), state changes arrive live.
  useEffect(() => {
    clearTimeout(pollTimer.current);
    if (job && isActiveJob(job)) {
      pollTimer.current = setTimeout(() => loadJob({ quiet: true }), POLL_MS);
    }
    return () => clearTimeout(pollTimer.current);
  }, [job, loadJob]);

  useEffect(() => {
    if (recomputeSignal) loadJob({ quiet: true });
  }, [recomputeSignal, loadJob]);

  const hasDiff =
    !!job && ['computed', 'awaiting_signoff', 'publishing', 'published'].includes(job.state);

  const loadDiff = useCallback(async () => {
    if (!hasDiff) return;
    setDiffLoading(true);
    try {
      const res = await getRecomputeDiff(id, { table, change, page });
      setDiff(splitDiffRows(res));
    } catch (err) {
      setError(errorMessage(err, 'Failed to load the diff.'));
    } finally {
      setDiffLoading(false);
    }
  }, [id, table, change, page, hasDiff]);

  useEffect(() => {
    loadDiff();
  }, [loadDiff]);

  const gate = publishGate({ job, user, canSignOff });
  const canDiscard = (canRun || canSignOff) && job && DISCARDABLE.includes(job.state);

  const errors = stageErrorsOf(job);
  const mustAcknowledge = needsErrorAcknowledgement(job);

  const openConfirm = (what: 'publish' | 'discard') => {
    setActionError(null);
    setAcknowledged(false);
    setConfirm(what);
  };

  const act = async () => {
    setBusy(true);
    setActionError(null);
    try {
      if (confirm === 'publish') {
        await signOffRecomputeJob(id, mustAcknowledge ? { acknowledgeErrors: acknowledged } : {});
        setNotice(
          mustAcknowledge
            ? 'Signed off, with its stage errors acknowledged under your name. The engine is publishing the diff; signed-off trips become amendment requests.'
            : 'Signed off. The engine is publishing the diff; signed-off trips become amendment requests.'
        );
      } else {
        await discardRecomputeJob(id);
        setNotice('Discarded. History is untouched.');
      }
      setConfirm(null);
      await loadJob({ quiet: true });
      onChanged?.();
    } catch (err) {
      setActionError(
        errorMessage(
          err,
          confirm === 'publish' ? 'The job was not published.' : 'The job was not discarded.'
        )
      );
      // The server's view of the errors may be newer than the page's (409 errors_not_acknowledged).
      if ((err as { response?: { data?: { code?: string } } }).response?.data?.code === 'errors_not_acknowledged')
        await loadJob({ quiet: true });
    } finally {
      setBusy(false);
    }
  };

  if (loading && !job) return <LoadingState label="Loading the recompute job..." />;
  if (!job)
    return (
      <>
        <ErrorBanner message={error} />
        <Link to="/lexistar/recompute-jobs" className={buttonClasses.secondary}>
          <FaArrowLeft size={11} /> All recompute jobs
        </Link>
      </>
    );

  const cards = headlineCards(job.diff);
  const tables: DiffTableCount[] = diff.tables.length
    ? diff.tables
    : [...new Set(diff.rows.map((r) => r.table_name))].map((t) => ({ table_name: t }));
  const estimate = job.estimate || {};
  const progress = job.progress || {};

  return (
    <div className="space-y-5">
      <ErrorBanner message={error} onDismiss={() => setError(null)} />
      <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />

      <div>
        <Link
          to="/lexistar/recompute-jobs"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          <FaArrowLeft size={10} /> All recompute jobs
        </Link>
      </div>

      <section className={`${cardClasses} p-4 sm:p-5`} aria-label="Job">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-slate-900">{triggerLabel(job.trigger)}</h2>
              <JobStateChip state={job.state} withErrors={completedWithErrors(job)} />
            </div>
            <p className="text-sm text-slate-600">
              {scopeSummary({ ...(job.scope || {}), ...(job.range ? { range: job.range } : {}) })}
            </p>
            <p className="text-xs text-slate-500">
              Requested by{' '}
              <span className="font-medium text-slate-700">{job.requested_by || '—'}</span> ·{' '}
              {fmtDateTime(job.created_at)}
              {job.config_snapshot_id && (
                <>
                  {' '}
                  · configuration <span className="font-mono">{job.config_snapshot_id}</span>
                </>
              )}
            </p>
            {job.signed_off_by && (
              <p className="text-xs text-slate-500">
                Signed off by{' '}
                <span className="font-medium text-slate-700">{job.signed_off_by}</span> ·{' '}
                {fmtDateTime(job.signed_off_at)}
              </p>
            )}
            {job.errors_acknowledged_by && (
              <p className="text-xs text-slate-500">
                Stage errors acknowledged by{' '}
                <span className="font-medium text-slate-700">{job.errors_acknowledged_by}</span> ·{' '}
                {fmtDateTime(job.errors_acknowledged_at)}
              </p>
            )}
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 lg:max-w-sm">
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => loadJob()}
                aria-label="Refresh"
                className={buttonClasses.secondary}
              >
                <FaSyncAlt size={11} />
              </button>
              {canDiscard && (
                <button
                  type="button"
                  onClick={() => openConfirm('discard')}
                  className={buttonClasses.danger}
                >
                  <FaTrashAlt size={11} /> Discard
                </button>
              )}
              <button
                type="button"
                onClick={() => openConfirm('publish')}
                disabled={!gate.allowed}
                aria-describedby={gate.reason ? 'publish-gate' : undefined}
                className={buttonClasses.primary}
              >
                <FaCheck size={11} /> Publish
              </button>
            </div>
            {gate.reason && job.state !== 'published' && (
              <p id="publish-gate" className="text-xs text-slate-600 sm:text-right">
                {gate.reason}
              </p>
            )}
          </div>
        </div>

        {isActiveJob(job) && job.state !== 'publishing' && (
          <div className="mt-4 space-y-1">
            <ProgressBar value={progressRatio(job)} label="Recompute progress" />
            <p className="text-xs text-slate-500 tabular-nums">
              {fmtNum(progress.partitions_done ?? 0)} of {fmtNum(progress.partitions_total ?? 0)}{' '}
              trucks · {fmtNum(progress.records_read ?? 0)} records read · estimate{' '}
              {fmtNum(estimate.positions)} positions
            </p>
          </div>
        )}
        {job.state === 'failed' && job.error && (
          <p className="mt-3 text-sm text-red-700">
            Failed: {job.error}. Current data was not touched.
          </p>
        )}
        {job.state === 'published' && (
          <p className="mt-3 text-sm text-slate-700">
            Published {fmtDateTime(job.published_at)}. This diff is kept permanently; superseded
            records are in the history schema.
          </p>
        )}
      </section>

      {errors && (
        <section
          className={`${cardClasses} p-4 sm:p-5 border-amber-300 bg-amber-50`}
          aria-label="Stage errors"
        >
          <h3 className="flex items-center gap-2 text-sm font-bold text-amber-800">
            <FaExclamationTriangle size={12} aria-hidden="true" />
            {completedWithErrors(job) ? 'Completed with errors' : 'Errors so far'} — {stageErrorSummary(errors)}
          </h3>
          <p className="text-sm text-slate-700 mt-1 mb-3">
            Part of the engine failed on some records while it rebuilt them. It kept going, so the
            difference below is complete for everything else, but the records of these trucks may
            be wrong where it failed.
            {mustAcknowledge && ' Whoever publishes it must confirm they have reviewed these errors.'}
          </p>
          <StageErrorList errors={errors} />
        </section>
      )}

      {cards.length > 0 && (
        <section
          aria-label="Headline deltas"
          className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3"
        >
          {cards.map((c) => (
            <KpiCard
              key={c.key}
              label={c.label}
              value={c.value}
              hint={c.hint}
              tone={c.warn ? 'warn' : 'default'}
            />
          ))}
        </section>
      )}

      {hasDiff && (
        <>
          {diff.signedOff.length > 0 && (
            <section
              className={`${cardClasses} p-4 sm:p-5 border-amber-300`}
              aria-label="Signed-off trips"
            >
              <h3 className="text-sm font-bold text-slate-900">
                Signed-off trips — proposed amendments ({diff.signedOff.length})
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                These trips are locked. Publishing does not change them; each becomes an amendment
                request that a second user approves on the trip.
              </p>
              <DiffTable rows={diff.signedOff} />
            </section>
          )}

          <section className={`${cardClasses} p-4 sm:p-5`} aria-label="Changed records">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <h3 className="text-sm font-bold text-slate-900">Changed records</h3>
              <SegmentedControl
                options={[
                  { value: 'table', label: 'Table' },
                  { value: 'map', label: 'Map' },
                ]}
                value={view}
                onChange={setView}
                ariaLabel="Diff view"
              />
            </div>
            <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Filter the diff">
              {[{ table_name: '' }, ...tables].map((t) => (
                <button
                  key={t.table_name || 'all'}
                  type="button"
                  aria-pressed={table === t.table_name}
                  onClick={() => {
                    setTable(t.table_name);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${table === t.table_name ? 'bg-slate-900 text-white border-slate-900' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {t.table_name ? TABLE_LABELS[t.table_name] || t.table_name : 'All tables'}
                  {t.count != null && (
                    <span className="ml-1 tabular-nums opacity-80">{t.count}</span>
                  )}
                </button>
              ))}
              <span className="w-px bg-slate-200 mx-1" aria-hidden="true" />
              {CHANGE_FILTERS.map((c) => (
                <button
                  key={c.value || 'any'}
                  type="button"
                  aria-pressed={change === c.value}
                  onClick={() => {
                    setChange(c.value);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${change === c.value ? 'bg-slate-900 text-white border-slate-900' : 'bg-surface text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {diffLoading ? (
              <div className="h-40 rounded-lg bg-slate-100 animate-pulse" aria-hidden="true" />
            ) : view === 'map' ? (
              <DiffMap rows={diff.rows} />
            ) : (
              <DiffTable rows={diff.rows} />
            )}
            {diff.pagination && Number(diff.pagination.totalPages || diff.pagination.pages) > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={buttonClasses.secondary}
                >
                  Previous
                </button>
                <span className="tabular-nums text-slate-600">
                  Page {page} of {diff.pagination.totalPages || diff.pagination.pages}
                </span>
                <button
                  type="button"
                  disabled={page >= Number(diff.pagination.totalPages || diff.pagination.pages)}
                  onClick={() => setPage((p) => p + 1)}
                  className={buttonClasses.secondary}
                >
                  Next
                </button>
              </div>
            )}
          </section>
        </>
      )}

      <Modal
        open={!!confirm}
        title={confirm === 'publish' ? 'Publish this recompute' : 'Discard this recompute'}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className={buttonClasses.secondary}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={act}
              disabled={busy || (confirm === 'publish' && mustAcknowledge && !acknowledged)}
              className={confirm === 'publish' ? buttonClasses.primary : buttonClasses.danger}
            >
              {busy ? 'Working…' : confirm === 'publish' ? 'Sign off and publish' : 'Discard'}
            </button>
          </>
        }
      >
        {confirm === 'publish' ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              You are the second user for this recompute (requested by{' '}
              <span className="font-medium">{job.requested_by}</span>). Publishing replaces the
              current records with the recomputed ones, keeps the old rows in history, re-anchors
              corrections and turns changes to{' '}
              <span className="tabular-nums font-semibold">
                {fmtNum(job.diff?.signed_off_trips_affected ?? diff.signedOff.length)}
              </span>{' '}
              signed-off trip(s) into amendment requests. It is written to the audit ledger under your
              name.
            </p>
            {mustAcknowledge && errors && (
              <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                  <FaExclamationTriangle size={12} aria-hidden="true" />
                  This recompute completed with errors — {stageErrorSummary(errors)}
                </p>
                <StageErrorList errors={errors} compact />
                <label className="flex items-start gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                  <span>
                    I have reviewed these errors and accept the recomputed records with them. My name
                    is recorded as having acknowledged them.
                  </span>
                </label>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-700">
            The shadow results are dropped and nothing in history changes. The job stays listed as
            discarded.
          </p>
        )}
        <InlineError message={actionError} />
      </Modal>
    </div>
  );
}
